import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, Invoice } from '../lib/api'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

export default function InvoicesPage() {
  const { data: invoices, isLoading, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => { const r = await api.invoices.list(); return r.data },
    refetchInterval: 15000,
  })

  if (isLoading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-gray-400">Loading invoices…</div>
    </div>
  )

  if (error) return (
    <div className="container mx-auto p-6">
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
        Failed to load invoices.
      </div>
    </div>
  )

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Invoices</h1>

      {!invoices || invoices.length === 0 ? (
        <div className="bg-white rounded-xl border text-center py-16">
          <p className="text-gray-400">No invoices yet.</p>
          <p className="text-gray-300 text-sm mt-1">Invoices appear automatically after a job is delivered.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv: Invoice) => (
            <Link key={inv.id} to={`/invoices/${inv.id}`}
              className="block bg-white rounded-xl border hover:border-blue-300 hover:shadow-sm transition p-4">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[inv.status] ?? 'bg-gray-100'}`}>
                      {inv.status}
                    </span>
                    <span className="font-mono text-sm text-gray-700 font-medium">{inv.invoiceNumber}</span>
                  </div>
                  {inv.dueDate && (
                    <div className="text-xs text-gray-400">Due: {new Date(inv.dueDate).toLocaleDateString()}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-blue-700">
                    ${typeof inv.amount === 'number' ? inv.amount.toLocaleString() : inv.amount}
                  </div>
                  <div className="text-xs text-gray-400">{inv.currency ?? 'USD'}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
